import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';

// Pages
import { HomePage } from './pages/HomePage';
import { BrowsePage } from './pages/BrowsePage';
import { BookDetailPage } from './pages/BookDetailPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { AuthCallback } from './pages/AuthCallback';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordPages';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { CheckoutSuccessPage, CheckoutCancelPage } from './pages/CheckoutResultPages';
import { DashboardPage } from './pages/DashboardPage';
import { OrdersPage } from './pages/OrdersPage';
import { LibraryPage } from './pages/LibraryPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';

// Admin Pages
import { AdminLayout, AdminDashboardPage } from './pages/admin/AdminDashboard';
import { AdminBooksPage } from './pages/admin/AdminBooksPage';
import { AdminOrdersPage, AdminUsersPage } from './pages/admin/AdminOrdersUsers';
import { AdminNewsletterPage } from './pages/admin/AdminNewsletterPage';
import { AdminSocialPage } from './pages/admin/AdminSocialPage';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';

import './App.css';

// Hide emergent badge
const HideEmergentBadge = () => {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      #emergent-badge,
      [id="emergent-badge"],
      div[class*="emergent"],
      .emergent-badge {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    // Also try to remove the element directly
    const removeEmergentBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.remove();
      }
    };

    removeEmergentBadge();
    
    // Run multiple times to catch dynamically added elements
    const interval = setInterval(removeEmergentBadge, 1000);
    setTimeout(() => clearInterval(interval), 10000);

    return () => {
      clearInterval(interval);
      document.head.removeChild(style);
    };
  }, []);

  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Protected Route
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Layout component that handles session_id in hash
const AppLayout = ({ children, showNavbar = true }) => {
  return (
    <div className="flex flex-col min-h-screen">
      {showNavbar && <Navbar />}
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

// Router component that checks for session_id before rendering routes
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id - process BEFORE normal routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<AppLayout><HomePage /></AppLayout>} />
      <Route path="/browse" element={<AppLayout><BrowsePage /></AppLayout>} />
      <Route path="/book/:bookId" element={<AppLayout><BookDetailPage /></AppLayout>} />
      <Route path="/login" element={<AppLayout><LoginPage /></AppLayout>} />
      <Route path="/register" element={<AppLayout><RegisterPage /></AppLayout>} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/forgot-password" element={<AppLayout><ForgotPasswordPage /></AppLayout>} />
      <Route path="/reset-password" element={<AppLayout><ResetPasswordPage /></AppLayout>} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Cart - accessible but requires auth to add items */}
      <Route path="/cart" element={<AppLayout><CartPage /></AppLayout>} />

      {/* Protected Routes */}
      <Route path="/checkout" element={
        <ProtectedRoute>
          <AppLayout><CheckoutPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/checkout/success" element={
        <ProtectedRoute>
          <AppLayout><CheckoutSuccessPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/checkout/cancel" element={
        <ProtectedRoute>
          <AppLayout><CheckoutCancelPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout><DashboardPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute>
          <AppLayout><OrdersPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/orders/:orderId" element={
        <ProtectedRoute>
          <AppLayout><OrdersPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/library" element={
        <ProtectedRoute>
          <AppLayout><LibraryPage /></AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/verify-email" element={
        <ProtectedRoute>
          <AppLayout><VerifyEmailPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminLayout />
        </AdminRoute>
      }>
        <Route index element={<AdminDashboardPage />} />
        <Route path="books" element={<AdminBooksPage />} />
        <Route path="orders" element={<AdminOrdersPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="newsletter" element={<AdminNewsletterPage />} />
        <Route path="social" element={<AdminSocialPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <HideEmergentBadge />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
